// 30-min open-slot picker for the reschedule sub-view. Reads the doctor's own open slots from the
// API (which subtracts PTO/blocks + existing bookings); a stale pick is rejected server-side (409).
// Presents a horizontal day picker → the selected day's slots (no endless multi-day wall).
import { useState } from "react";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { formatDay, formatTime } from "../lib/format";
import { Loading, ErrorState, EmptyState } from "./ui";

const HORIZON_DAYS = 14;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function groupByDay(slots: string[]): [string, string[]][] {
  const map = new Map<string, string[]>();
  for (const s of slots) {
    const day = formatDay(s);
    map.set(day, [...(map.get(day) ?? []), s]);
  }
  return [...map.entries()];
}

export function SlotGrid({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (iso: string) => void;
}) {
  const api = useApi();
  const from = new Date().toISOString();
  const to = new Date(Date.now() + HORIZON_DAYS * 24 * 60 * 60 * 1000).toISOString();
  const { data, loading, error } = useAsync(() => api.availabilitySlots(from, to), []);
  const [activeDay, setActiveDay] = useState<string | null>(null);

  if (loading) return <Loading label="Finding open times…" />;
  if (error) return <ErrorState message={error} />;
  const slots = (data as { slots: string[] } | null)?.slots ?? [];
  if (!slots.length) return <EmptyState>No open times in the next two weeks.</EmptyState>;

  const days = groupByDay(slots);
  const current = activeDay && days.some(([d]) => d === activeDay) ? activeDay : days[0]![0];
  const daySlots = days.find(([d]) => d === current)?.[1] ?? [];

  return (
    <div className="slots">
      <div className="slots__days">
        {days.map(([day, ds]) => {
          const d = new Date(ds[0]!);
          return (
            <button
              key={day}
              type="button"
              className={`slots__day-pill ${current === day ? "is-active" : ""}`}
              onClick={() => setActiveDay(day)}
            >
              <span className="slots__day-pill-dow">{DOW[d.getDay()]}</span>
              <span className="slots__day-pill-date">{d.getDate()}</span>
              <span className="slots__day-pill-count">{ds.length} open</span>
            </button>
          );
        })}
      </div>
      <div className="slots__grid slots__grid--scroll">
        {daySlots.map((s) => (
          <button
            key={s}
            type="button"
            className={`slot ${value === s ? "is-selected" : ""}`}
            onClick={() => onChange(s)}
          >
            {formatTime(s)}
          </button>
        ))}
      </div>
    </div>
  );
}
